import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { useSelector } from 'react-redux';
import { DEFAULT_HOME_USE_LABEL } from "../constants"
import { isPC } from '../utils';
import { useDispatch } from 'react-redux';
import { setSummaryPanelOpen, setSummaryExpanded } from '../redux_toolkit/reserveSlice';

dayjs.locale('ja');

/**
 * 現在の選択内容パネル
 *
 * PC (769px以上) : 画面左端に固定パネルとして表示
 * スマホ (768px以下) : 画面下部に固定バーとして表示
 *
 * Props:
 *   showNote (bool) - true のとき「※予約の残り枠数は変動します...」をPCパネルに表示
 *                     SelectTime.jsx だけ true を渡す
 */
const SelectionSummary = function ({ showNote }) {
  const dispatch = useDispatch();
  // スマホバーの展開/折りたたみ状態、PCパネルの開閉状態
  // 画面遷移をまたいで保持したいため、コンポーネントの useState ではなく Redux (reserve スライス) で管理する
  const isExpanded = useSelector((state) => state.reserve.summaryExpanded);
  const isPanelOpen = useSelector((state) => state.reserve.summaryPanelOpen);

  // PCパネルの top を動的に設定するための ref
  const panelRef = useRef(null);

  // 開閉時の高さアニメーション用: カード本体の実際の高さ（開いている時の高さ）
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);

  // Redux store から選択済みデータを取得
  const business = useSelector((state) => state.reserve.business);
  const recItem = useSelector((state) => state.reserve.recItem); // 選択中の申請項目リスト
  const place = useSelector((state) => state.reserve.place);
  const useHome = useSelector((state) => state.reserve.useHome);
  const hasSelectedPlace = useSelector((state) => state.reserve.hasSelectedPlace);
  const rsvDate = useSelector((state) => state.reserve.rsvDate);
  const rsvDayOfWeek = useSelector((state) => state.reserve.rsvDayOfWeek);
  const rsvDateWithTime = useSelector((state) => state.reserve.rsvDateWithTime);
  // rsvConfig スライスから locationSetting を取得（自宅ラベルのカスタマイズに使用）
  const locationSetting = useSelector((state) => state.rsvConfig.locationSetting);

  // PCパネルの top / left を #contents に合わせて動的に設定する
  // useLayoutEffect = ブラウザ描画前に同期実行されるため、チラつきが起きない
  useLayoutEffect(() => {
    const updatePosition = () => {
      const contents = document.getElementById('contents');
      if (contents && panelRef.current) {
        const rect = contents.getBoundingClientRect();

        // ---- top ----
        // 「最初の画面へ」ボタン（MoveTop, className="moveTopWrapper"）の上端に揃える。
        // 見つからない場合（構成が異なる画面など）は #contents の上端にフォールバックする
        const moveTop = contents.querySelector('.moveTopWrapper');
        const topRect = moveTop ? moveTop.getBoundingClientRect() : rect;

        // getBoundingClientRect().top はビューポート基準の値
        // SPAではページ遷移後もスクロール位置が残ることがある
        // → window.scrollY を加算して「ドキュメント先頭からの絶対Y座標」に変換する
        const absoluteTop = topRect.top + window.scrollY;

        // ---- left ----
        // #contents の左端から「パネル幅 + 16px」だけ左に配置する
        // → 画面幅が変わっても常に #contents の真横に表示される
        const panelWidth = panelRef.current.offsetWidth || 220;
        const absoluteLeft = rect.left + window.scrollX - panelWidth - 16;

        panelRef.current.style.top = `${absoluteTop}px`;
        // 画面左端からはみ出さないよう最小値 8px を保証する
        panelRef.current.style.left = `${Math.max(8, absoluteLeft)}px`;
      }
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  // ---- 用件名の取得 ----
  // Redux の business は2パターンの構造で保存されることがある
  //
  // パターン1（SelectBusinessから保存）:
  //   { [businessId]: { businessName: '妊娠・出産相談', ... } }
  //   → キーが ID で、値がオブジェクト
  //
  // パターン2（Top.jsx から直接保存）:
  //   { businessId: '1', businessName: '妊娠・出産相談', ... }
  //   → フラット構造で businessName が直接プロパティに存在
  //
  // どちらのパターンにも対応するため、2段階でチェックする
  let businessName = '';
  const businessKeys = Object.keys(business);
  for (let i = 0; i < businessKeys.length; i += 1) {
    const val = business[businessKeys[i]];
    // パターン1: 値がオブジェクトで businessName を持つ
    if (typeof val === 'object' && val !== null && val.businessName) {
      businessName = val.businessName;
      break;
    }
  }
  // パターン2: business 自体に直接 businessName がある
  if (!businessName && business.businessName) {
    businessName = String(business.businessName);
  }

  // ---- 場所名の取得 ----
  // hasSelectedPlace = false のとき: まだ SelectPlace を通過していない → 場所を表示しない
  // hasSelectedPlace = true かつ useHome = true: 「自宅から利用する」を選択済み → "自宅" を表示
  // hasSelectedPlace = true かつ useHome = false: 施設 or 電話を選択済み → place.name を表示
  //
  // ※ RSV_FLAG_HOME = 0 は Redux 初期値 (rsvFlag: 0) と同じ数値のため、
  //    rsvFlag で自宅選択を判定できない。hasSelectedPlace で区別している。
  let placeName = '';
  const otherPlace = place && place.name ? place.name : '';
  if (hasSelectedPlace) {
    // locationSetting に home_use が設定されていればその文言を使う。なければ '自宅' にフォールバック
    const homeLabel = locationSetting?.item_label?.home_use || DEFAULT_HOME_USE_LABEL;
    placeName = useHome ? homeLabel : otherPlace;
  }

  // ---- 利用日のフォーマット ----
  // "2026-05-18" → "2026年5月18日(月)"
  const formattedDate = rsvDate
    ? `${dayjs(rsvDate).format('YYYY[年]M[月]D[日]')}(${rsvDayOfWeek})`
    : '';

  // ---- 利用時間のフォーマット ----
  // rsvDateWithTime は "2026-05-18 09:00" という形式で保存される
  // dayjs で時刻部分だけを取り出して "9時00分" に変換する
  const formattedTime = rsvDateWithTime ? dayjs(rsvDateWithTime).format('H[時]mm[分]') : '';

  // 選択中の申請項目名を配列で取得
  const recItemNames = recItem.map((item) => item.recItemName);
  const recItemNamesKey = recItemNames.join(',');

  // PCパネルの開閉アニメーション（上下）用: カード本体の実際の高さを測定する。
  // 表示項目数によって高さが変わるため、内容が変わるたびに測り直す。
  // useLayoutEffect = 描画前に同期実行されるため、閉じている状態から開いた瞬間の高さもズレなく取得できる
  useLayoutEffect(() => {
    if (cardRef.current) {
      setCardHeight(cardRef.current.scrollHeight);
    }
  }, [businessName, placeName, formattedDate, formattedTime, recItemNamesKey]);

  // 表示できるものが何もなければ何も描画しない
  // (SelectPlace の初回表示など、まだ何も選んでいない状態)
  const hasAny = businessName || placeName || formattedDate || formattedTime;
  if (!hasAny) return null;

  // スマホバー用の表示テキスト: 値があるものだけ "/" でつなぐ（折りたたみ時1行表示用）
  const recItemText = recItemNames.join('・');
  const mobileItems = [businessName, recItemText, placeName, formattedDate, formattedTime]
    .filter(Boolean)
    .join(' / ');

  // 展開表示用: 2行目（用件名 / 申請項目名 を "/" でつなぐ）
  // 例: "用件A① / 禁煙～"
  // 申請項目がない場合は用件名のみ表示
  const titleText = recItemNames.length > 0
    ? [businessName, recItemText].filter(Boolean).join(' / ')
    : businessName;

  return (
    <>
      {/* ==============================
          PC 用: 左端固定パネル
          CSS で min-width: 769px のときだけ表示
          ============================== */}
      <div
        className={`selectionSummaryPC${isPanelOpen ? '' : ' selectionSummaryPC--closed'}`}
        ref={panelRef}
      >
        {/* メインカード（水色背景）。閉じているときは高さだけ縮んで細い帯として残る */}
        {/* maxHeight はJSで測定した実際の高さ（cardHeight）を使い、閉じた時はCSS側の帯の高さ(32px)と合わせる */}
        <div
          className="selectionSummaryPC__card"
          ref={cardRef}
          style={{ maxHeight: isPanelOpen ? (cardHeight || undefined) : 32 }}
        >
          {/* 中身はまとめてフェードアウトさせる。幅を固定してあるので、カードが縮んでも折り返さない */}
          <div className="selectionSummaryPC__cardBody">
            {/* 「現在の選択」 バッジ（青い丸ラベル） */}
            <span className="selectionSummaryPC__badge">現在の選択</span>

            {/* 選択済みの各項目 */}
            <div className="selectionSummaryPC__list">
              {businessName && (
                <div>
                  <p className="selectionSummaryPC__label">用件</p>
                  <p className="selectionSummaryPC__value">{businessName}</p>
                </div>
              )}
              {/* 選択中の申請項目を用件の直下に表示 */}
              {recItemNames.length > 0 && (
                <div>
                  <p className="selectionSummaryPC__label">申請項目</p>
                  {recItemNames.map((name, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <p key={index} className="selectionSummaryPC__value">
                      {name}
                    </p>
                  ))}
                </div>
              )}
              {placeName && (
                <div>
                  <p className="selectionSummaryPC__label">場所</p>
                  <p className="selectionSummaryPC__value">{placeName}</p>
                </div>
              )}
              {formattedDate && (
                <div>
                  <p className="selectionSummaryPC__label">利用日</p>
                  <p className="selectionSummaryPC__value">{formattedDate}</p>
                </div>
              )}
              {formattedTime && (
                <div>
                  <p className="selectionSummaryPC__label">利用時間</p>
                  <p className="selectionSummaryPC__value">{formattedTime}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 境界線に浮く開閉ボタン（カードの高さが縮むのに合わせて、下端に合わせた位置のまま一緒に移動する） */}
        {/* PC実機では表示しない（isPC() はデバイス判定のため、画面幅を変えても表示されない） */}
        {/* {!isPC() && ( */}
        <button
          className="selectionSummaryPC__edgeToggle"
          onClick={() => dispatch(setSummaryPanelOpen(!isPanelOpen))}
          aria-label={isPanelOpen ? 'パネルを閉じる' : 'パネルを開く'}
          type="button"
        >
          {/* 開いているときは上向き「⌃」、閉じているときはCSSで-90度回転させて下向き「⌄」にする */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 6 9 12 15 18" />
          </svg>
        </button>
        {/* )} */}
      </div>

      {/* ==============================
          スマホ用: 下部固定バー
          CSS で max-width: 768px のときだけ表示
          展開（デフォルト）: カレンダーアイコン + 3段テキスト
          折りたたみ: 1行サマリー表示
          ============================== */}
      <div className={`selectionSummaryMobile${isExpanded ? '' : ' selectionSummaryMobile--min'}`}>
        {/* 折りたたみ/展開ボタン（右上角に絶対配置） */}
        <button
          className="selectionSummaryMobile__toggle"
          onClick={() => dispatch(setSummaryExpanded(!isExpanded))}
          aria-label="表示切り替え"
          type="button"
        >
          {/* 展開時は下向き(▼)、折りたたみ時はCSSで回転して上向き(▲) */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {/* 展開（デフォルト）: カレンダーアイコン + テキスト3段 */}
        <div className="selectionSummaryMobile__default">
          <div className="selectionSummaryMobile__txt">
            <span className="selectionSummaryMobile__minBadge">選択中</span>
            {/* 1行目: 日付 + 時間 + 場所（値があるときだけ表示） */}
            {(formattedDate || formattedTime || placeName) && (
              <div className="selectionSummaryMobile__meta">
                {[formattedDate, formattedTime, placeName].filter(Boolean).join('　')}
              </div>
            )}
            {/* 2行目: 用件名 / 申請項目名（申請項目がない場合は用件名のみ） */}
            <div className="selectionSummaryMobile__title">{titleText}</div>
          </div>
        </div>

        {/* 折りたたみ（1行）: "選択中" | サマリーテキスト */}
        <div className="selectionSummaryMobile__minView">
          <span className="selectionSummaryMobile__minBadge">選択中</span>
          <div className="selectionSummaryMobile__oneline">{mobileItems}</div>
        </div>

      </div>
    </>
  );
};

SelectionSummary.propTypes = {
  showNote: PropTypes.bool,
};

SelectionSummary.defaultProps = {
  showNote: false,
};

export default SelectionSummary;
